import { Component } from '@theme/component';
import { lockScroll, prefersReducedMotion, unlockScroll } from '@theme/utilities';

const COOKIE_NAME = 'age_verified';
const EXPAND_DURATION_MS = 1000;

/**
 * Age verification gate — circular overlay with confirm/decline actions.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} circle
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} yesButton
 * @property {HTMLAnchorElement} noButton
 *
 * @extends {Component<Refs>}
 */
class AgeVerification extends Component {
  requiredRefs = ['circle', 'content', 'yesButton', 'noButton'];

  /** @type {boolean} */
  #isConfirming = false;

  /** @type {number | undefined} */
  #expandTimeout;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isVerified()) {
      document.documentElement.classList.add('age-verified');
      document.documentElement.classList.remove('age-gate-pending', 'age-gate-active');
      this.remove();
      return;
    }

    this.hidden = false;
    document.documentElement.classList.add('age-gate-active', 'age-gate-pending');
    document.documentElement.classList.remove('age-verified');
    lockScroll(this);
    this.#animateIn();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#expandTimeout) window.clearTimeout(this.#expandTimeout);
    unlockScroll(this);
    document.documentElement.classList.remove('age-gate-active', 'age-gate-pending', 'age-gate-revealing');
  }

  /**
   * Confirm age — set cookie, expand circle, then reveal page.
   */
  confirmAge = () => {
    if (this.#isConfirming) return;
    this.#isConfirming = true;

    this.#setCookie();

    const { circle, content, yesButton, noButton } = this.refs;
    yesButton.disabled = true;
    noButton.setAttribute('aria-disabled', 'true');

    if (prefersReducedMotion()) {
      this.classList.add('age-verification--fade-out');
      this.#expandTimeout = window.setTimeout(() => this.#teardown(), 350);
      return;
    }

    content.classList.add('age-verification__content--fade');
    circle.classList.add('age-verification__circle--expand');
    this.classList.add('age-verification--expanding');

    this.#expandTimeout = window.setTimeout(() => {
      document.documentElement.classList.add('age-gate-revealing', 'age-verified');
      this.#teardown();
    }, EXPAND_DURATION_MS);
  };

  /**
   * Decline — redirect without setting the cookie so the gate returns on next visit.
   * @param {MouseEvent} event
   */
  declineAge = (event) => {
    if (this.#isConfirming) {
      event.preventDefault();
      return;
    }

    const url = this.dataset.noUrl;
    if (!url) {
      event.preventDefault();
    }
  };

  #teardown() {
    unlockScroll(this);
    document.documentElement.classList.remove('age-gate-active', 'age-gate-pending', 'age-gate-revealing');
    document.documentElement.classList.add('age-verified');
    this.remove();
  }

  #animateIn() {
    if (prefersReducedMotion()) {
      this.classList.add('age-verification--visible');
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('age-verification--enter');
      });
    });
  }

  /** @returns {boolean} */
  #isVerified() {
    return document.cookie.split(';').some((part) => part.trim().startsWith(`${COOKIE_NAME}=`));
  }

  #setCookie() {
    const days = Number(this.dataset.cookieDays) || 30;
    const maxAge = Math.max(1, days) * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=1; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  }
}

if (!customElements.get('age-verification')) {
  customElements.define('age-verification', AgeVerification);
}
